import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { DocumentDuplicateIcon, PrinterIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { validationRules } from '../../utils/formValidation';

const ResetPasswordModal = ({ open, onClose, user, onSubmit }) => {
  const [autoGenerate, setAutoGenerate] = useState(true);
  const [requireChange, setRequireChange] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');

  const {
    register,
    handleSubmit: rhfHandleSubmit,
    reset,
    formState: { errors },
  } = useForm({ mode: 'onChange' });

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    const array = new Uint32Array(12);
    crypto.getRandomValues(array);
    return Array.from(array, (v) => chars[v % chars.length]).join('');
  };

  const onFormSubmit = async (data) => {
    setLoading(true);
    try {
      const finalPassword = autoGenerate ? generatePassword() : data.password;
      const userId = user.id || user._id;

      if (!userId) {
        throw new Error('Invalid user data');
      }

      await onSubmit({
        userId,
        password: finalPassword,
        requireChange
      });

      setGeneratedPassword(finalPassword);
      setShowResult(true);
    } catch (error) {
      // Error handled by parent via onSubmit rejection
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedPassword);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '', 'width=600,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Password Reset Details</title>
          <style>
            body { font-family: Arial; padding: 20px; }
            .container { border: 1px solid #ccc; padding: 20px; }
          </style>
        </head>
        <body>
          <div class="container">
            <h2>Password Reset Details</h2>
            <p><strong>User:</strong> ${user?.email}</p>
            <p><strong>Temporary Password:</strong> ${generatedPassword}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleCloseModal = () => {
    setShowResult(false);
    setGeneratedPassword('');
    reset();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={!showResult ? handleCloseModal : null}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white dark:bg-[#1a1a1a] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <button
            onClick={handleCloseModal}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 focus:outline-none"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          {!showResult ? (
            <form onSubmit={rhfHandleSubmit(onFormSubmit)}>
              <div className="bg-white dark:bg-[#1a1a1a] px-4 pt-5 pb-4 sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Reset password
                    </h3>

                    <div className="mt-4">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                        {user?.email}
                      </p>

                      <div className="space-y-4">
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="auto-generate"
                              type="checkbox"
                              checked={autoGenerate}
                              onChange={(e) => setAutoGenerate(e.target.checked)}
                              className="h-4 w-4 text-orange-600 border-gray-300 dark:border-gray-700 dark:bg-[#2a2a2a] rounded focus:ring-orange-500"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="auto-generate" className="font-medium text-gray-700 dark:text-gray-300">
                              Automatically create a password
                            </label>
                          </div>
                        </div>

                        {!autoGenerate && (
                          <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              New Password
                            </label>
                            <input
                              type="password"
                              id="password"
                              {...register('password', autoGenerate ? {} : validationRules.passwordStrong)}
                              className={`mt-1 block w-full shadow-sm sm:text-sm rounded-md border-gray-300 dark:border-gray-700 dark:bg-[#2a2a2a] dark:text-white focus:ring-orange-500 focus:border-orange-500 ${
                                errors.password ? 'border-red-300' : ''
                              }`}
                            />
                            {errors.password && (
                              <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
                            )}
                          </div>
                        )}

                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="require-change"
                              type="checkbox"
                              checked={requireChange}
                              onChange={(e) => setRequireChange(e.target.checked)}
                              className="h-4 w-4 text-orange-600 border-gray-300 dark:border-gray-700 dark:bg-[#2a2a2a] rounded focus:ring-orange-500"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="require-change" className="font-medium text-gray-700 dark:text-gray-300">
                              Require this user to change their password when they first sign in
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-[#111111] px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex justify-center rounded-md px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {loading ? (
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : 'Reset Password'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md px-4 py-2 bg-white dark:bg-[#2a2a2a] text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#222222] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="bg-white dark:bg-[#1a1a1a] px-4 pt-5 pb-4 sm:p-6">
                <div className="text-center sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                    Password has been reset
                  </h3>

                  <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4 border border-gray-200 dark:border-gray-800">
                    <div className="mb-4">
                      <div className="flex justify-between py-2">
                        <span className="font-medium text-gray-700 dark:text-gray-300">User</span>
                        <span className="text-gray-900 dark:text-white">{user?.email}</span>
                      </div>
                      <hr className="my-2 dark:border-gray-700" />
                      <div className="flex justify-between py-2">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Password</span>
                        <div className="flex items-center">
                          <code className="text-gray-900 dark:text-white mr-2 bg-gray-100 dark:bg-[#2a2a2a] px-2 py-1 rounded">{generatedPassword}</code>
                          <button
                            onClick={handleCopy}
                            className="text-gray-500 hover:text-orange-600 dark:text-gray-400 dark:hover:text-orange-500 focus:outline-none"
                            title="Copy to clipboard"
                          >
                            <DocumentDuplicateIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={handlePrint}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 dark:border-gray-700 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-[#2a2a2a] hover:bg-gray-50 dark:hover:bg-[#222222] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                    >
                      <PrinterIcon className="h-5 w-5 mr-2" />
                      Print
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-[#111111] px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="w-full inline-flex justify-center rounded-md px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordModal;
